import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/apple-design-system.css';
import './apple-login.css';
import ThemeToggle from '@/components/apple-ui/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LoginPageApple = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      let data;
      try {
        data = await response.clone().json();
      } catch {
        data = {};
      }

      if (response.ok) {
        localStorage.setItem('token', data.token);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        if (data.plan_info) localStorage.setItem('plan_info', JSON.stringify(data.plan_info));
        // Handle must_change_password for invited users
        if (data.must_change_password) {
          if (data.pending_invitation_token) {
            localStorage.setItem('pending_invitation_token', data.pending_invitation_token);
          }
          toast.info('Please set a new password to continue.');
          navigate('/forgot-password', { state: { email: data.user?.email, needsPassword: true, mustChange: true } });
          return;
        }
        // Handle pending invitation - only redirect if this user was the one invited
        const pendingToken = localStorage.getItem('pending_invitation_token');
        if (pendingToken) {
          localStorage.removeItem('pending_invitation_token');
          // Only redirect to accept-invitation if email matches invited email
          // (backend sets must_change_password only for invited users)
          if (data.must_change_password === false && data.user) {
            // Regular user who happens to have a pending token stored - ignore it
          } else if (data.user) {
            navigate(`/accept-invitation?token=${pendingToken}`);
            return;
          }
        }
        if (data.pending_invitation_token) {
          localStorage.setItem('pending_invitation_token', data.pending_invitation_token);
        }
        toast.success('Welcome back!');
        if (onLogin) onLogin(data.token, data.user, data.plan_info);
        const invToken = data.pending_invitation_token || localStorage.getItem('pending_invitation_token');
        if (invToken && !data.must_change_password) {
          localStorage.removeItem('pending_invitation_token');
          navigate(`/accept-invitation?token=${invToken}`);
          return;
        }
        const redirectTo = searchParams.get('redirect') || '/';
        navigate(redirectTo);
      } else if (response.status === 403) {
        // User exists but has no password — redirect to forgot password
        const needsEmail = data?.detail?.email || email;
        toast.info('Please set a password for your account.');
        navigate('/forgot-password', { state: { email: needsEmail, needsPassword: true } });
      } else {
        const msg = typeof data?.detail === 'string' ? data.detail : 'Invalid credentials';
        setError(msg);
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-apple" data-testid="apple-login-page">
      <div className="login-container-apple">
        {/* Theme toggle */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
          <ThemeToggle />
        </div>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{
            width: '44px',
            height: '44px',
            background: '#000',
            borderRadius: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginBottom: '12px',
            boxShadow: isDarkMode
              ? '0 0 0 2px #ffffff'
              : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)'
          }}>
            <img
              src="/logo-marketing.png"
              alt="t09n.com"
              style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: 0, display: 'block' }}
            />
          </div>
          <span style={{
            fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontStyle: 'normal',
            fontSize: '18px',
            color: isDarkMode ? '#ffffff' : '#000000',
            letterSpacing: '-0.3px',
            lineHeight: 1.1
          }}>
            t09n<span style={{ fontWeight: 700 }}>.com</span>
          </span>
          <span style={{
            fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif',
            marginTop: '3px',
            fontSize: '11px',
            color: isDarkMode ? '#ffffff' : '#000000',
            fontWeight: 700,
            fontStyle: 'oblique',
            lineHeight: 1.2,
            letterSpacing: '0.1px'
          }}>
            The Operating System For Translators
          </span>
        </div>

        {/* Login Form */}
        <div className="form-container-apple" data-testid="login-form-card">
          <form onSubmit={handleLogin} data-testid="login-form">
            {/* Error */}
            {error && (
              <div className="error-banner-apple" data-testid="login-error-message">
                {error}
              </div>
            )}

            {/* Email */}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              required
              autoComplete="email"
              className="input-field-apple"
              data-testid="login-email-input"
            />

            {/* Password */}
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                autoComplete="current-password"
                className="input-field-apple"
                style={{ paddingRight: '48px' }}
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '16px', top: '14px', background: 'none', border: 'none', cursor: 'pointer', color: '#86868b', padding: 0 }}
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Forgot password link */}
            <div style={{ textAlign: 'right', marginBottom: '8px', marginTop: '-8px' }}>
              <Link
                to="/forgot-password"
                style={{ fontSize: '13px', color: '#007AFF', textDecoration: 'none', fontWeight: '500' }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary-apple"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><Loader2 size={18} className="animate-spin" style={{ marginRight: '8px' }} /> Signing in...</>
              ) : (
                'Continue with Email'
              )}
            </button>
          </form>

          {/* Sign up link */}
          <div className="links-apple">
            Don&apos;t have an account?{' '}
            <Link to="/signup" data-testid="goto-signup-link">
              Create an account
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '32px', textAlign: 'center' }}>
          <p style={{ fontSize: '12px', color: 'var(--apple-text-tertiary, rgba(0,0,0,0.4))', margin: '0 0 4px 0' }}>
            &copy; 2026 t09n.com. All rights reserved.
          </p>
          <p style={{ fontSize: '11px', color: 'var(--apple-text-quaternary, rgba(0,0,0,0.3))', margin: '0 0 4px 0', fontStyle: 'italic' }}>
            Operated by Localization Lounge &middot; GSTIN: 24BSWPM3389R1ZG
          </p>
          <p style={{ fontSize: '11px', color: 'var(--apple-text-quaternary, rgba(0,0,0,0.3))', margin: '0' }}>
            Support:{' '}
            <a href="mailto:support@t09n.com" style={{ color: '#007AFF', textDecoration: 'none' }}>
              support@t09n.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export { LoginPageApple };
export default LoginPageApple;
